<script setup lang="ts">
// Old way vs the new way — same quality bar, a different machine.
// Content is prop-driven so the component can be reused on other pages; the
// defaults below preserve the original how-we-work copy.
const { el, isVisible } = useRevealAnimation({ threshold: 0.15 });

interface CompareRow {
  label: string;
  oldLabel?: string;
  old: string;
  now: string;
}

withDefaults(
  defineProps<{
    heading?: string;
    subheading?: string;
    nowEyebrow?: string;
    nowSub?: string;
    oldEyebrow?: string;
    oldSub?: string;
    rows?: CompareRow[];
  }>(),
  {
    heading: 'Why organizations switch to the factory',
    subheading: 'Same quality bar. A completely different machine.',
    nowEyebrow: 'Our Way',
    nowSub: 'WebReinvent AI factory',
    oldEyebrow: 'The Old Way',
    oldSub: 'Traditional agency',
    rows: () => [
      {
        label: 'Who writes the code',
        old: 'Junior-heavy team, seniors in review',
        now: 'Agents build, senior engineers audit',
      },
      {
        label: 'Testing',
        old: 'Manual QA after the build',
        now: 'Automated UAT during the build',
      },
      {
        label: 'Time to deploy',
        old: 'Weeks between a finished build and production',
        now: 'Continuous deploys — live in days, not weeks',
      },
      {
        label: 'Handoffs',
        old: '4-5 teams, queued between departments',
        now: 'One pipeline, one team, one log',
      },
      {
        label: 'Cost curve',
        old: 'Scales with headcount',
        now: 'Own infrastructure — cost stays down',
      },
      {
        label: 'Sign-off',
        old: 'One review at the end, fingers crossed',
        now: 'Every stage human-verified',
      },
    ],
  }
);
</script>

<template>
  <div class="cmp-root">
    <!-- header -->
    <div class="text-center">
      <h2 class="text-3xl lg:text-[40px]">
        <WrGradientText :text="heading" append-class="py-2" />
      </h2>
      <p class="mx-auto mt-3 max-w-2xl text-lg text-[var(--foreground)] opacity-70">
        {{ subheading }}
      </p>
    </div>

    <!-- card -->
    <div ref="el" :class="{ in: isVisible }" class="mx-auto mt-12 max-w-5xl">
      <div
        class="cmp-card relative overflow-hidden rounded-2xl border border-black/10 dark:border-white/10"
      >
        <!-- desktop divider -->
        <div class="cmp-divider"></div>

        <!-- VS badge between the two headers -->
        <div class="cmp-vs" aria-hidden="true">vs</div>

        <div role="table" aria-label="WebReinvent AI factory vs traditional delivery">
          <!-- header row -->
          <div role="row" class="grid grid-cols-1 lg:grid-cols-2">
            <!-- header: now (WebReinvent way first) -->
            <div role="columnheader" class="cmp-cell cmp-now-hdr cmp-hdr-line">
              <div class="flex items-center gap-3 px-5 py-5 lg:px-6">
                <span class="cmp-now-badge">
                  <svg
                    viewBox="0 0 24 24"
                    class="size-5"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M13 2 4 14h6l-1 8 9-12h-6z"></path>
                  </svg>
                </span>
                <div>
                  <div class="cmp-hdr-eyebrow-now">{{ nowEyebrow }}</div>
                  <div class="cmp-hdr-sub-now">{{ nowSub }}</div>
                </div>
              </div>
            </div>

            <!-- header: old -->
            <div role="columnheader" class="cmp-cell cmp-old-hdr cmp-hdr-line">
              <div class="flex items-center gap-3 px-5 py-5 lg:px-6">
                <span class="cmp-old-badge">
                  <svg
                    viewBox="0 0 24 24"
                    class="size-5"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="9"></circle>
                    <path d="M12 7v5l3 2"></path>
                  </svg>
                </span>
                <div>
                  <div class="cmp-hdr-eyebrow-old">{{ oldEyebrow }}</div>
                  <div class="cmp-hdr-sub">{{ oldSub }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- data rows -->
          <div
            v-for="(r, i) in rows"
            :key="r.label"
            role="row"
            class="grid grid-cols-1 lg:grid-cols-2"
          >
            <div role="cell" class="cmp-cell cmp-now" :class="i !== rows.length - 1 ? 'cmp-rowline' : ''">
              <div class="flex items-start gap-3 px-5 py-4 lg:px-6">
                <span class="cmp-now-ic">
                  <svg
                    viewBox="0 0 24 24"
                    class="size-3.5"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M5 12l5 5L19 7"></path>
                  </svg>
                </span>
                <div class="min-w-0">
                  <div class="cmp-data-label cmp-data-label-now">{{ r.label }}</div>
                  <div class="cmp-now-text">{{ r.now }}</div>
                </div>
              </div>
            </div>
            <div role="cell" class="cmp-cell cmp-old" :class="i !== rows.length - 1 ? 'cmp-rowline' : ''">
              <div class="flex items-start gap-3 px-5 py-4 lg:px-6">
                <span class="cmp-old-ic">
                  <svg
                    viewBox="0 0 24 24"
                    class="size-3.5"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18"></path>
                    <path d="m6 6 12 12"></path>
                  </svg>
                </span>
                <div class="min-w-0">
                  <div class="cmp-data-label">{{ r.oldLabel ?? r.label }}</div>
                  <div class="cmp-old-text">{{ r.old }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Accent for the "now" (positive) column. Defaults to the WebReinvent purple
   (how-we-work); the vaah-agents page overrides these to its blue-indigo.
   The dark rule omits `html` so a page-scoped override wins on specificity. */
.cmp-root {
  --cmp-accent: #613faa;
  --cmp-accent-2: #af66e4;
}
.my-app-dark .cmp-root {
  --cmp-accent: #af66e4;
  --cmp-accent-2: #af66e4;
}

.cmp-card {
  opacity: 0;
  transform: translateY(12px);
  transition:
    opacity 0.6s ease,
    transform 0.6s ease;
  background: rgba(255, 255, 255, 0.9);
}
html.my-app-dark .cmp-card {
  background: rgba(9, 4, 12, 0.85);
}
.in .cmp-card {
  opacity: 1;
  transform: translateY(0);
}

/* columns */
.cmp-old {
  background: rgba(0, 0, 0, 0.02);
}
html.my-app-dark .cmp-old {
  background: rgba(255, 255, 255, 0.02);
}
.cmp-now {
  background: color-mix(in srgb, var(--cmp-accent) 5%, transparent);
}
html.my-app-dark .cmp-now {
  background: color-mix(in srgb, var(--cmp-accent) 7%, transparent);
}
.cmp-old-hdr {
  background: rgba(0, 0, 0, 0.035);
}
html.my-app-dark .cmp-old-hdr {
  background: rgba(255, 255, 255, 0.035);
}
.cmp-now-hdr {
  background:
    radial-gradient(120px 60px at 20% 0%, color-mix(in srgb, var(--cmp-accent-2) 16%, transparent), transparent 70%),
    color-mix(in srgb, var(--cmp-accent) 7%, transparent);
}
html.my-app-dark .cmp-now-hdr {
  background:
    radial-gradient(120px 60px at 20% 0%, color-mix(in srgb, var(--cmp-accent-2) 22%, transparent), transparent 70%),
    color-mix(in srgb, var(--cmp-accent) 10%, transparent);
}

.cmp-hdr-line {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
html.my-app-dark .cmp-hdr-line {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
@media (min-width: 1024px) {
  .cmp-rowline {
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  }
  html.my-app-dark .cmp-rowline {
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }
}

/* divider (desktop only) */
.cmp-divider {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 1px;
  background: rgba(0, 0, 0, 0.08);
  display: none;
}
html.my-app-dark .cmp-divider {
  background: rgba(255, 255, 255, 0.12);
}
@media (min-width: 1024px) {
  .cmp-divider {
    display: block;
  }
}

.cmp-vs {
  position: absolute;
  left: 50%;
  top: 88px;
  transform: translate(-50%, -50%);
  z-index: 20;
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 9999px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--cmp-accent);
}
html.my-app-dark .cmp-vs {
  background: #0a0216;
  border-color: rgba(255, 255, 255, 0.15);
  color: var(--cmp-accent);
}
@media (min-width: 1024px) {
  .cmp-vs {
    display: none;
  }
}

/* header text */
.cmp-hdr-eyebrow-old {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: rgba(0, 0, 0, 0.55);
}
html.my-app-dark .cmp-hdr-eyebrow-old {
  color: rgba(255, 255, 255, 0.6);
}
.cmp-hdr-eyebrow-now {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--cmp-accent);
}
html.my-app-dark .cmp-hdr-eyebrow-now {
  color: var(--cmp-accent);
}
.cmp-hdr-sub {
  margin-top: 3px;
  font-size: 20px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.75);
}
html.my-app-dark .cmp-hdr-sub {
  color: rgba(255, 255, 255, 0.75);
}
.cmp-hdr-sub-now {
  margin-top: 3px;
  font-size: 20px;
  font-weight: 800;
  color: rgba(0, 0, 0, 0.9);
}
html.my-app-dark .cmp-hdr-sub-now {
  color: rgba(255, 255, 255, 0.95);
}

/* header badges */
.cmp-old-badge {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.05);
  color: rgba(0, 0, 0, 0.55);
  flex-shrink: 0;
}
html.my-app-dark .cmp-old-badge {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.65);
}
.cmp-now-badge {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--cmp-accent), var(--cmp-accent-2));
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 4px 14px color-mix(in srgb, var(--cmp-accent) 35%, transparent);
}
html.my-app-dark .cmp-now-badge {
  background: linear-gradient(135deg, var(--cmp-accent), var(--cmp-accent-2));
  box-shadow: 0 4px 16px color-mix(in srgb, var(--cmp-accent) 40%, transparent);
}

/* row text */
.cmp-data-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: rgba(0, 0, 0, 0.55);
}
html.my-app-dark .cmp-data-label {
  color: rgba(255, 255, 255, 0.55);
}
.cmp-data-label-now {
  color: rgba(0, 0, 0, 0.5);
}
html.my-app-dark .cmp-data-label-now {
  color: rgba(255, 255, 255, 0.45);
}
.cmp-old-text {
  margin-top: 3px;
  font-size: 14px;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.8);
}
html.my-app-dark .cmp-old-text {
  color: rgba(255, 255, 255, 0.78);
}
.cmp-now-text {
  margin-top: 3px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.9);
}
html.my-app-dark .cmp-now-text {
  color: rgba(255, 255, 255, 0.92);
}

/* row icons */
.cmp-old-ic {
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.05);
  color: rgba(0, 0, 0, 0.5);
  flex-shrink: 0;
  margin-top: 1px;
}
html.my-app-dark .cmp-old-ic {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
}
.cmp-now-ic {
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: 9999px;
  background: var(--cmp-accent);
  color: #fff;
  flex-shrink: 0;
  margin-top: 1px;
}
html.my-app-dark .cmp-now-ic {
  background: var(--cmp-accent);
}
</style>
